/**
 * Following (migration 1058; PRD-tasks-asks-and-my-work FR-5).
 *
 * What the current person follows: an item, a cycle, a track, a session, a
 * decision or a commitment. Rows live in `watches` (one per person × record),
 * read by their owner and written only through follow / unfollow. Followers are
 * told when the record changes: an item by its audit events (864), the others by
 * the governance events that name them, once a day (1058).
 *
 * The item calls (watchItem, unwatchItem, isWatching, watchedItemIds) are kept
 * for the item page; they are the same rows with subject_type 'item'.
 */

import { create } from 'zustand';
import { getCurrentWorkspaceId, getSafeSupabaseClient, isSupabaseAvailable } from '../../lib/supabaseSync';
import { useUserStore } from './userStore';

export type FollowSubject = 'item' | 'cycle' | 'track' | 'session' | 'decision' | 'commitment';

const key = (type: FollowSubject, id: string) => `${type}:${id}`;

interface WatchesState {
  /** `${subject_type}:${subject_id}` for everything the current person follows. */
  followed: Set<string>;
  /** Item ids the current person follows (the item page reads this). */
  watchedItemIds: Set<string>;
  loading: boolean;
  /** The last refusal, in the database's words. */
  error: string | null;

  fetchFromSupabase: () => Promise<void>;
  follow: (type: FollowSubject, id: string) => Promise<void>;
  unfollow: (type: FollowSubject, id: string) => Promise<void>;
  isFollowing: (type: FollowSubject, id: string) => boolean;

  watchItem: (itemId: string) => Promise<void>;
  unwatchItem: (itemId: string) => Promise<void>;
  isWatching: (itemId: string) => boolean;
}

function itemIds(followed: Set<string>): Set<string> {
  return new Set([...followed].filter((k) => k.startsWith('item:')).map((k) => k.slice(5)));
}

export const useWatchesStore = create<WatchesState>((set, get) => {
  const apply = (followed: Set<string>) => set({ followed, watchedItemIds: itemIds(followed) });

  async function write(type: FollowSubject, id: string, on: boolean) {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    const before = get().followed;
    const next = new Set(before);
    if (on) next.add(key(type, id));
    else next.delete(key(type, id));
    apply(next);
    const { error } = await supabase.rpc(on ? 'follow' : 'unfollow', { p_subject_type: type, p_subject_id: id });
    if (error) {
      apply(before);
      set({ error: error.message });
      console.warn('[WatchesStore] follow refused:', error.message);
    } else {
      set({ error: null });
    }
  }

  return {
    followed: new Set(),
    watchedItemIds: new Set(),
    loading: false,
    error: null,

    fetchFromSupabase: async () => {
      if (!isSupabaseAvailable()) return;
      const supabase = getSafeSupabaseClient();
      const userId = useUserStore.getState().userId;
      const workspaceId = getCurrentWorkspaceId();
      if (!supabase || !userId || !workspaceId) return;
      set({ loading: true });
      try {
        const { data, error } = await supabase
          .from('watches')
          .select('subject_type, subject_id')
          .eq('user_id', userId)
          .eq('workspace_id', workspaceId);
        if (error) {
          console.warn('[WatchesStore] fetch failed:', error.message);
          return;
        }
        apply(new Set((data ?? []).map((r: { subject_type: string; subject_id: string }) => `${r.subject_type}:${r.subject_id}`)));
      } finally {
        set({ loading: false });
      }
    },

    follow: (type, id) => write(type, id, true),
    unfollow: (type, id) => write(type, id, false),
    isFollowing: (type, id) => get().followed.has(key(type, id)),

    watchItem: (itemId) => write('item', itemId, true),
    unwatchItem: (itemId) => write('item', itemId, false),
    isWatching: (itemId) => get().followed.has(key('item', itemId)),
  };
});
